import React from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { useCrmLeadStages } from '../../hooks/useCrmLeadStages';

interface SingleProps {
    value: string;
    onChange: (v: string) => void;
    style?: React.CSSProperties;
    placeholder?: string;
    disabled?: boolean;
}

export const CrmLeadStagePicker: React.FC<SingleProps> = ({ value, onChange, style, placeholder, disabled }) => {
    const { stages, fallback, loading } = useCrmLeadStages();
    const knownValues = new Set(stages.map(s => s.value));
    const showOrphan = !!value && !loading && !knownValues.has(value);

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <select
                value={value}
                onChange={e => onChange(e.target.value)}
                style={style}
                disabled={disabled || loading}
            >
                <option value="">{placeholder ?? '— pick stage —'}</option>
                {stages.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                ))}
                {showOrphan && (
                    <option value={value}>{value} (not in CRM)</option>
                )}
            </select>
            {showOrphan && (
                <AlertTriangle
                    size={14}
                    color="var(--warning, #d97706)"
                    aria-label={`Saved value "${value}" not found in current CRM stages`}
                />
            )}
            {fallback && !loading && (
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>(default list)</span>
            )}
        </span>
    );
};

interface MultiProps {
    value: string[];
    onChange: (v: string[]) => void;
    selectStyle?: React.CSSProperties;
    addButtonStyle?: React.CSSProperties;
}

export const CrmLeadStageMultiPicker: React.FC<MultiProps> = ({ value, onChange, selectStyle, addButtonStyle }) => {
    const updateAt = (idx: number, v: string) => {
        const next = [...value];
        next[idx] = v;
        onChange(next);
    };
    const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx));
    const add = () => onChange([...value, '']);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {value.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No stages selected — sequence will trigger on any stage change.</div>
            )}
            {value.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CrmLeadStagePicker
                        value={v}
                        onChange={nv => updateAt(i, nv)}
                        style={selectStyle}
                    />
                    <button
                        type="button"
                        onClick={() => removeAt(i)}
                        aria-label="Remove stage"
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4 }}
                    >
                        <X size={14} />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                style={addButtonStyle ?? { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px dashed var(--stroke)', color: 'var(--text-secondary)', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', alignSelf: 'flex-start' }}
            >
                <Plus size={12} /> Add stage
            </button>
        </div>
    );
};
